/**
 * Folds the `journalAbbreviation` alias into `container-title-short` when the
 * abbreviated form is present but the short-title field is not. `journalAbbreviation`
 * is left untouched on the patch - both fields persist, the alias is only
 * hidden from the form because the CSL schema exposes `container-title-short`,
 * not `journalAbbreviation`. Never folds the other way: an explicit
 * `container-title-short` always wins and is never overwritten.
 * @param patch - The CSL patch (already spread into a fresh object) to fold.
 * @returns The patch, with `container-title-short` filled in from the alias when applicable.
 */
const foldJournalAbbreviationAlias = (
  patch: Record<string, unknown>,
): Record<string, unknown> => {
  const alias = patch['journalAbbreviation'];
  const shortTitle = patch['container-title-short'];

  const hasAlias = typeof alias === 'string' && alias.length > 0;
  const hasShortTitle = typeof shortTitle === 'string' && shortTitle.length > 0;

  if (!hasAlias || hasShortTitle) {
    return patch;
  }

  return {
    ...patch,
    'container-title-short': alias,
  };
};

/**
 * Normalizes the CSL patch type to the desired format.
 * If the patch type is empty or missing, it will be normalized to 'document'.
 * Also folds the `journalAbbreviation` alias into `container-title-short`
 * (see `foldJournalAbbreviationAlias`) - this runs on the same incoming-patch
 * path, before the form engine judges field emptiness.
 * @param patch - The CSL patch to normalize.
 * @returns The normalized CSL patch.
 */
export const normalizeCslPatchType = (patch: object | null | undefined): object | null => {
  if (!patch || typeof patch !== 'object') {
    return patch ?? null;
  }

  const cslPatched = foldJournalAbbreviationAlias({ ...patch } as Record<string, unknown>);
  const cslType: string = typeof cslPatched.type === 'string' ? cslPatched.type : '';

  // document is a catch-all type - https://docs.citationstyles.org/en/stable/specification.html#document
  if (cslType === '') {
    return {
      ...cslPatched,
      type: 'document',
    };
  }

  return cslPatched;
};
